import type { ApiRequestWith } from "@/api/types";
import { httpPublic2 } from "@/api/public";
import { logOut } from "@/redux/actions/logout";
import store from "@/redux/store";

// store.dispatch is typed as Dispatch<AnyAction> but auth actions are thunks
const dispatch: (action: any) => any = store.dispatch;

type NewIdTokenPayload = {
  id_token?: string;
  refresh_token?: string;
};

type NewIdTokenResponse = {
  AuthenticationResult: {
    AccessToken: string;
    IdToken: string;
  };
};

type AuthRequestsTyped = {
  newIdToken: ApiRequestWith<NewIdTokenResponse, { body: NewIdTokenPayload }>;
};

export const authRequestsTyped: AuthRequestsTyped = {
  // Uses httpPublic2 (public, no auth hooks) with an explicit Authorization header so
  // tokenRefresh.ts can call this without creating a circular dependency through
  // the authenticated ky instances.
  newIdToken: function ({ body }) {
    const token = localStorage.getItem("token");

    if (!token) {
      dispatch(logOut());
      throw new Error("Missing token");
    }

    // retry config — the backend collapses all failures (including transient
    // Cognito errors and Lambda cold-starts) into a single 401 "Invalid refresh
    // token", making it impossible to distinguish transient from genuine expiry
    // by status code alone. One retry before giving up matches the resilience of
    // the old axios implementation and absorbs the common case of a single-
    // attempt blip without masking a genuinely expired refresh token.
    return httpPublic2
      .post("v1/auth/new_id_token", {
        headers: { Authorization: token },
        json: body,
        retry: { limit: 1, methods: ["post"], statusCodes: [401, 408, 429, 500, 502, 503, 504] },
      })
      .json<NewIdTokenResponse>();
  },
};
